import { getResetDate, parseCreditDenial } from '../../common/credits-backend/denial';
import type { CreditsBackendSnapshot, CreditWarning } from '../../common/credits-backend/types';
import { ErrorWithCause, normalizeErrorCause, type ErrorCause } from '../../common/errors';
import type { JsonValue } from '../../common/json';
import { isNonEmptyString, isRecord, isValidTimestamp } from '../../common/validation';

type CreditsBackendFetchResponse = {
	ok: boolean;
	status: number;
	statusText: string;
	headers?: { get: (name: string) => string | null };
	json: () => Promise<JsonValue>;
};

type CreditsBackendFetchInit = {
	method: 'GET' | 'POST';
	credentials: 'include';
	signal?: AbortSignal;
	headers: Record<string, string>;
};

export type CreditsBackendFetch = (
	input: string,
	init: CreditsBackendFetchInit,
) => Promise<CreditsBackendFetchResponse>;

/** Product the request is made from. Reported to the backend as `X-Product`. */
export type CreditsProduct =
	| 'atlassian-agent-studio'
	| 'atlassian-studio'
	| 'bitbucket'
	| 'confluence'
	| 'goals'
	| 'home'
	| 'jira'
	| 'jsm'
	| 'loom'
	| 'projects'
	| 'rovo'
	| 'trello';

export type CreditsBackendGetCreditsStateOptions = {
	/**
	 * Optional request cancellation signal. Callers that own refresh cadence can
	 * pass a timeout-backed signal so a hung backend request does not block the
	 * current polling cycle indefinitely.
	 */
	signal?: AbortSignal;
};

export type CreditsBackendClientOptions = {
	product: CreditsProduct;
	cloudId?: string;
	/** Bitbucket workspace ID, sent as X-Workspace-Id. */
	workspaceId?: string;
	/** Rovo agent ID, sent as the `agentId` query parameter. */
	agentId?: string;
	fetch?: CreditsBackendFetch;
	endpoint?: string;
};

export type CreditRequestResult =
	| { outcome: 'accepted'; requestId: string }
	| { outcome: 'unavailable'; status: number }
	| { outcome: 'rate-limited'; retryAfterSeconds?: number }
	| { outcome: 'failed'; status?: number };

export type CreditsBackendClient = {
	requestCredits: (options?: CreditsBackendGetCreditsStateOptions) => Promise<CreditRequestResult>;
	getCreditsState: (
		options?: CreditsBackendGetCreditsStateOptions,
	) => Promise<CreditsBackendSnapshot>;
};

export class CreditsBackendClientError extends ErrorWithCause {
	constructor(message: string, cause?: ErrorCause) {
		super(message, cause !== undefined ? { cause } : undefined);
		this.name = 'CreditsBackendClientError';
	}
}

const DEFAULT_CREDITS_BACKEND_PATH = '/gateway/api/assist/api/rovo/v1/credits/state';

const EXPERIENCE_ID = 'ai-mate';

// The warning is advisory, so a malformed one is dropped instead of rejecting the response.
const parseCreditWarning = (warning: JsonValue | undefined): CreditWarning | undefined => {
	if (!isRecord(warning) || warning.scope !== 'ORGANIZATION') {
		return undefined;
	}

	const resetsAt = getResetDate(warning.resetsAt);
	return { scope: 'ORGANIZATION', ...(resetsAt !== undefined ? { resetsAt } : {}) };
};

const withWarning = (
	snapshot: Omit<CreditsBackendSnapshot, 'warning'>,
	warning: JsonValue | undefined,
): CreditsBackendSnapshot => {
	const parsedWarning = snapshot.denial === null ? parseCreditWarning(warning) : undefined;
	return parsedWarning === undefined ? snapshot : { ...snapshot, warning: parsedWarning };
};

export const parseCreditsBackendSnapshot = (value: JsonValue): CreditsBackendSnapshot | null => {
	if (!isRecord(value)) {
		return null;
	}

	const { asOf, updatedAt, expiresAt, denial, warning } = value;
	if (
		!isValidTimestamp(asOf) ||
		!isValidTimestamp(updatedAt) ||
		!isValidTimestamp(expiresAt) ||
		expiresAt <= updatedAt ||
		denial === undefined
	) {
		return null;
	}

	if (denial === null) {
		return withWarning({ asOf, updatedAt, expiresAt, denial: null }, warning);
	}

	return isRecord(denial)
		? { asOf, updatedAt, expiresAt, denial: parseCreditDenial(denial) }
		: null;
};

/** Adapt the pre-flight response to the existing session and consumer state model. */
const parseCreditStateResponse = (value: JsonValue): CreditsBackendSnapshot | null => {
	if (!isRecord(value)) {
		return null;
	}

	const { asOf, refreshAfterSeconds, denial, warning } = value;
	if (
		!isValidTimestamp(asOf) ||
		typeof refreshAfterSeconds !== 'number' ||
		!Number.isFinite(refreshAfterSeconds) ||
		refreshAfterSeconds <= 0 ||
		(denial != null && !isRecord(denial))
	) {
		return null;
	}

	// Schedule relative to receipt, not the server clock (which may be skewed).
	const updatedAt = Date.now();
	const expiresAt = updatedAt + refreshAfterSeconds * 1000;
	if (!isValidTimestamp(expiresAt) || expiresAt <= updatedAt) {
		return null;
	}

	return withWarning(
		{
			asOf,
			updatedAt,
			expiresAt,
			denial: isRecord(denial) ? parseCreditDenial(denial) : null,
		},
		warning,
	);
};

const withAgentId = (endpoint: string, agentId: string | undefined): string => {
	const trimmedAgentId = agentId?.trim();
	if (!trimmedAgentId) {
		return endpoint;
	}

	return `${endpoint}${endpoint.includes('?') ? '&' : '?'}agentId=${encodeURIComponent(
		trimmedAgentId,
	)}`;
};

const resolveCreditsBackendFetch = (
	fetchOverride: CreditsBackendFetch | undefined,
): CreditsBackendFetch => {
	if (fetchOverride !== undefined) {
		return fetchOverride;
	}

	if (typeof globalThis.fetch !== 'function') {
		throw new CreditsBackendClientError('Credits backend fetch is unavailable.');
	}

	return globalThis.fetch.bind(globalThis) as CreditsBackendFetch;
};

export const createCreditsBackendClient = ({
	product,
	cloudId,
	workspaceId,
	agentId,
	fetch: fetchOverride,
	endpoint = DEFAULT_CREDITS_BACKEND_PATH,
}: CreditsBackendClientOptions): CreditsBackendClient => {
	if (!isNonEmptyString(endpoint)) {
		throw new CreditsBackendClientError('Credits backend endpoint is empty or blank.');
	}

	const normalizedEndpoint = withAgentId(endpoint.trim(), agentId);
	const creditsBackendFetch = resolveCreditsBackendFetch(fetchOverride);

	const headers: Record<string, string> = {
		Accept: 'application/json',
		'X-Product': product,
		'X-Experience-Id': EXPERIENCE_ID,
	};
	if (cloudId?.trim()) {
		headers['Atl-Cloudid'] = cloudId.trim();
		headers['X-Cloudid'] = cloudId.trim();
	}

	if (workspaceId?.trim()) {
		headers['X-Workspace-Id'] = workspaceId.trim();
	}

	return {
		requestCredits: async ({ signal } = {}) => {
			if (!cloudId?.trim()) {
				return { outcome: 'unavailable', status: 400 };
			}
			try {
				const response = await creditsBackendFetch(
					withAgentId('/gateway/api/assist/api/rovo/v1/credits/requests', agentId),
					{
						method: 'POST',
						credentials: 'include',
						headers,
						...(signal !== undefined ? { signal } : {}),
					},
				);
				if (response.status === 202) {
					const payload = await response.json();
					return isRecord(payload) && isNonEmptyString(payload.requestId)
						? { outcome: 'accepted', requestId: payload.requestId }
						: { outcome: 'failed', status: 202 };
				}
				if (response.status === 429) {
					const raw = response.headers?.get('Retry-After');
					const seconds = raw?.trim() ? Number(raw) : NaN;
					return {
						outcome: 'rate-limited',
						...(Number.isFinite(seconds) && seconds >= 0 ? { retryAfterSeconds: seconds } : {}),
					};
				}
				if ([400, 401, 403, 404, 409].includes(response.status)) {
					return { outcome: 'unavailable', status: response.status };
				}
				return { outcome: 'failed', status: response.status };
			} catch {
				return { outcome: 'failed' };
			}
		},
		getCreditsState: async ({ signal }: CreditsBackendGetCreditsStateOptions = {}) => {
			let response: CreditsBackendFetchResponse;
			try {
				response = await creditsBackendFetch(normalizedEndpoint, {
					method: 'GET',
					credentials: 'include',
					headers: headers,
					...(signal !== undefined ? { signal } : {}),
				});
			} catch (cause) {
				throw new CreditsBackendClientError(
					'Credits backend request failed.',
					normalizeErrorCause(cause),
				);
			}

			if (response.status !== 200) {
				// Pre-flight is advisory: clear a previous block on HTTP errors. The
				// action backend remains responsible for enforcement at time of use.
				const now = Date.now();
				return {
					asOf: now,
					updatedAt: now,
					expiresAt: now + 60_000,
					denial: null,
				};
			}

			let payload: JsonValue;
			try {
				payload = await response.json();
			} catch (cause) {
				throw new CreditsBackendClientError(
					'Credits backend response was not valid JSON.',
					normalizeErrorCause(cause),
				);
			}
			const snapshot = parseCreditStateResponse(payload);
			if (snapshot === null) {
				throw new CreditsBackendClientError('Credits backend response shape was invalid.');
			}

			return snapshot;
		},
	};
};
